/**
 * Pinia Store для управления каталогами Primar
 * 
 * State: хранит загруженные данные каталогов, статус загрузки, ошибки
 * Actions: загрузить, обновить, очистить кэш
 * Getters: получить данные, проверить статус
 */

import { defineStore } from 'pinia'
import * as catalogService from '@/services/catalogService'

export const useCatalogStore = defineStore('catalog', {
  state: () => ({
    countries: null,
    products: null,
    enc: null,
    encreplaces: null,
    loading: {
      countries: false,
      products: false,
      enc: false,
      encreplaces: false
    },
    error: {
      countries: null,
      products: null,
      enc: null,
      encreplaces: null
    },
    catalogVersion: null,
    lastUpdateTime: null,
    cacheStatus: null
  }),

  getters: {
    /**
     * Получить каталог по типу
     */
    getCatalog: (state) => (type) => {
      return state[type] || null
    },

    /**
     * Проверить, загружается ли каталог
     */
    isLoading: (state) => (type) => {
      if (type) {
        return state.loading[type] ?? false
      }
      return Object.values(state.loading).some(v => v)
    },

    /**
     * Получить ошибку для каталога
     */
    getError: (state) => (type) => {
      if (type) {
        return state.error[type] || null
      }
      return state.error
    },

    /**
     * Проверить, загружены ли все каталоги
     */
    isAllLoaded: (state) => {
      return state.countries && state.products && state.enc && state.encreplaces
    },

    /**
     * Получить замены ENC ячеек по оригинальному коду
     */
    getEncReplace: (state) => (original) => {
      if (!state.encreplaces) return null
      return state.encreplaces.find(item => item.original === original) || null
    },

    /**
     * Получить все замены ENC
     */
    getAllEncReplaces: (state) => state.encreplaces || [],

    /**
     * Получить версию каталога
     */
    getCatalogVersion: (state) => state.catalogVersion,

    /**
     * Получить статус кэша
     */
    getCacheStatus: (state) => state.cacheStatus
  },

  actions: {
    /**
     * Загрузить один тип каталога
     * @param {string} type - Тип: 'countries', 'products', 'enc', 'encreplaces'
     */
    async fetchCatalog(type) {
      this.loading[type] = true
      this.error[type] = null

      try {
        const data = await catalogService.getCatalogData(type)
        this[type] = data
        this.lastUpdateTime = new Date().toISOString()
      } catch (error) {
        console.error(`Error fetching ${type}:`, error)
        this.error[type] = error.message
        throw error
      } finally {
        this.loading[type] = false
      }
    },

    /**
     * Загрузить все каталоги (countries, products, enc, encreplaces)
     */
    async fetchAllCatalogs() {
      try {
        await Promise.all([
          this.fetchCatalog('countries'),
          this.fetchCatalog('products'),
          this.fetchCatalog('enc'),
          this.fetchCatalog('encreplaces')
        ])
      } catch (error) {
        console.error('Error fetching all catalogs:', error)
        throw error
      }
    },

    /**
     * Обновить каталог (очистить кэш и перезагрузить)
     * @param {string|null} type - Если null, обновить все
     */
    async refreshCatalog(type = null) {
      catalogService.clearCatalogCache(type)
      
      if (type) {
        await this.fetchCatalog(type)
      } else {
        await this.fetchAllCatalogs()
      }
    },

    /**
     * Обновить информацию о статусе кэша
     */
    updateCacheStatus() {
      this.cacheStatus = catalogService.getCacheStatus()
    },

    /**
     * Очистить кэш
     * @param {string|null} type - Если null, очистить все
     */
    clearCache(type = null) {
      catalogService.clearCatalogCache(type)
      this.updateCacheStatus()
    }
  }
})
